import { Component, inject, Input, OnInit, signal, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { ToastService } from '../../../core/services/toast.service';
import { VendorsService } from '../../../core/services/vendors.service';
import { FormValidationService } from '../../../core/services/form-validation.service';
import { authActions } from '../../../core/store/auth';
import { ApiError } from '../../../core/models/common/api-error';
import { FormFieldsetCardComponent } from '../../../shared/components/form-fieldset-card/form-fieldset-card.component';
import { User } from '../../../core/models/entities/user';
import { SharedModule } from '../../../shared/shared.module';
import { SharedManagerModule } from '../../../shared-manager/shared-manager.module';

@Component({
  selector: 'app-vendor-billing-settings',
  template: `
    <app-fieldset-card
      [showSaveButton]="true"
      [saving]="saving()"
      [trackFormChanges]="form"
      (onSave)="save()"
    >
      <div title>Informations de facturation</div>
      <div description>
        Les informations de facturation sont utilisées pour générer les
        factures.
      </div>

      <app-text-input
        [control]="form.controls.iban"
        name="iban"
        label="IBAN"
      />

      <app-text-input
        label="N° de TVA"
        name="vat"
        [control]="form.controls.vat"
      ></app-text-input>
    </app-fieldset-card>
  `,
  imports: [SharedModule, SharedManagerModule],
})
export class VendorBillingSettingsComponent implements OnInit {
  @Input() authUser!: User;

  @ViewChild(FormFieldsetCardComponent)
  fieldsetCard: FormFieldsetCardComponent;

  private store = inject(Store);
  private toastService = inject(ToastService);
  private vendorsService = inject(VendorsService);
  private formValidationService = inject(FormValidationService);

  saving = signal<boolean>(false);

  form = new FormGroup({
    iban: new FormControl<string>(''),
    vat: new FormControl<string>(''),
    name: new FormControl<string>('', Validators.required), // Nécessaire pour l'API
    slug: new FormControl<string>('', Validators.required), // Nécessaire pour l'API
  });

  ngOnInit() {
    this.form.patchValue({
      iban: this.authUser.vendor.iban || '',
      vat: this.authUser.vendor.vat || '',
      name: this.authUser.vendor.name || '',
      slug: this.authUser.vendor.slug || '',
    });
  }

  async save() {
    if (!this.formValidationService.validate(this.form)) return;

    this.saving.set(true);
    try {
      const vendor = await this.vendorsService.update({
        name: this.form.value.name,
        slug: this.form.value.slug,
        iban: this.form.value.iban,
        vat: this.form.value.vat,
      });
      this.updateUserStore(vendor);
      this.toastService.success('Informations de facturation enregistrées.');

      // Réinitialiser le dirty state
      this.fieldsetCard?.resetDirtyState();
    } catch (exception) {
      this.formValidationService.displayApiValidationErrors(
        this.form,
        exception as ApiError,
      );
    } finally {
      this.saving.set(false);
    }
  }

  private updateUserStore(vendor: any) {
    this.store.dispatch(
      authActions.findCurrentUserSuccess({
        user: {
          ...this.authUser,
          vendor: { ...this.authUser.vendor, ...vendor },
        },
      }),
    );
  }
}
